import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, FileText, Download, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignerContratPage() {
  const { token } = useParams();
  const [contrat, setContrat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nomSignataire, setNomSignataire] = useState('');
  const [qualite, setQualite] = useState('');
  const [accepte, setAccepte] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/contrats-eta/public/sign/${token}`);
        if (!r.ok) throw new Error('Lien invalide');
        const d = await r.json();
        setContrat(d);
        if (d.statut === 'signe') setSigned(true);
      } catch (e) {
        toast.error("Lien invalide ou expiré");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSign = async () => {
    if (!nomSignataire || !accepte) {
      toast.error("Remplissez votre nom et acceptez les conditions");
      return;
    }
    setSigning(true);
    try {
      const r = await fetch(`${API}/api/contrats-eta/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom_signataire: nomSignataire, qualite, accept_conditions: true }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur signature');
      }
      setSigned(true);
      toast.success('Contrat signé avec succès !');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Chargement...</div>;
  if (!contrat) return <div className="p-10 text-center text-red-600">Contrat introuvable ou lien expiré.</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-6 pb-6 border-b-2 border-emerald-600">
            <Shield className="h-8 w-8 text-emerald-700" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-emerald-800">{contrat.titre || 'Contrat de prestations'}</h1>
              <p className="text-sm text-gray-500">N° {contrat.numero} &mdash; {contrat.prestataire?.nom}</p>
            </div>
          </div>

          {signed ? (
            <div className="text-center py-12" data-testid="sign-success">
              <CheckCircle className="h-20 w-20 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-emerald-800">Contrat signé avec succès</h2>
              <p className="text-gray-600 mt-2">Merci ! Une copie signée a été envoyée à {contrat.client?.email}.</p>
              <a
                href={`${API}/api/contrats-eta/public/sign/${token}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                data-testid="btn-telecharger-pdf-signe"
              >
                <Download className="h-5 w-5" />Télécharger le contrat signé
              </a>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-xs uppercase text-emerald-700 font-semibold mb-1">Prestataire</p>
                  <p className="font-bold">{contrat.prestataire?.nom}</p>
                  <p className="text-sm text-gray-600">{contrat.prestataire?.siret && `SIRET ${contrat.prestataire.siret}`}</p>
                  <p className="text-sm text-gray-600">{contrat.prestataire?.adresse}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs uppercase text-blue-700 font-semibold mb-1">Client</p>
                  <p className="font-bold">{contrat.client?.prenom} {contrat.client?.nom}</p>
                  <p className="text-sm text-gray-600">{contrat.client?.siret && `SIRET ${contrat.client.siret}`}</p>
                  <p className="text-sm text-gray-600">{contrat.client?.adresse} {contrat.client?.ville}</p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold mb-3">Prestations</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border">
                    <thead className="bg-emerald-700 text-white">
                      <tr>
                        <th className="p-2 text-left">Prestation</th>
                        <th className="p-2 text-right">Qté</th>
                        <th className="p-2 text-right">Unité</th>
                        <th className="p-2 text-right">P.U. HT</th>
                        <th className="p-2 text-right">Total HT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(contrat.prestations_v2 || []).map((p, i) => {
                        const base = (p.quantite || 0) * (p.prix_unitaire_ht || 0);
                        const total = p.remise_percent ? base * (1 - p.remise_percent / 100) : base;
                        return (
                          <tr key={i} className={i % 2 ? 'bg-gray-50' : ''}>
                            <td className="p-2">
                              <p className="font-semibold">{p.libelle}</p>
                              <p className="text-xs text-gray-500">{p.categorie}{p.notes ? ` — ${p.notes}` : ''}</p>
                            </td>
                            <td className="p-2 text-right">{p.quantite}</td>
                            <td className="p-2 text-right">{p.unite}</td>
                            <td className="p-2 text-right">{(p.prix_unitaire_ht || 0).toFixed(2)} €</td>
                            <td className="p-2 text-right font-semibold">{total.toFixed(2)} €</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-emerald-50 font-bold">
                      <tr><td colSpan="4" className="p-2 text-right">Total HT</td><td className="p-2 text-right">{(contrat.totaux?.total_ht || 0).toFixed(2)} €</td></tr>
                      <tr><td colSpan="4" className="p-2 text-right">TVA 20%</td><td className="p-2 text-right">{(contrat.totaux?.total_tva || 0).toFixed(2)} €</td></tr>
                      <tr className="text-emerald-700 text-base"><td colSpan="4" className="p-2 text-right">TOTAL TTC</td><td className="p-2 text-right">{(contrat.totaux?.total_ttc || 0).toFixed(2)} €</td></tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="mb-6">
                <a
                  href={`${API}/api/contrats-eta/public/sign/${token}/pdf`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-emerald-700 hover:underline"
                  data-testid="btn-voir-pdf-complet"
                >
                  <FileText className="h-4 w-4" />Consulter le contrat PDF complet
                </a>
              </div>

              <Card className="border-emerald-300">
                <CardHeader className="bg-emerald-50"><CardTitle className="text-emerald-800">Signer en ligne</CardTitle></CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom et prénom du signataire *</label>
                    <Input data-testid="input-nom-signataire" value={nomSignataire} onChange={e => setNomSignataire(e.target.value)} placeholder="M. Dupond" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Qualité (gérant, propriétaire...)</label>
                    <Input data-testid="input-qualite" value={qualite} onChange={e => setQualite(e.target.value)} placeholder="Gérant" />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox data-testid="chk-accept-conditions" checked={accepte} onCheckedChange={setAccepte} />
                    <span className="text-sm">
                      J'ai lu et j'accepte les conditions du présent contrat. Je comprends qu'en cliquant sur "Signer", je m'engage juridiquement.
                    </span>
                  </label>
                  <Button
                    data-testid="btn-signer-contrat"
                    disabled={signing || !nomSignataire || !accepte}
                    onClick={handleSign}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                  >
                    {signing ? 'Signature en cours...' : 'Signer le contrat'}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
